import React, { useContext, useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { ProjectContext } from "../../context/ProjectProvider";
import { getTemplatesAPI, deleteTemplateAPI } from "../../utils/api";
import "./HeaderTable.css";
import { PdfHighlightContext } from "../../context/ProjectProvider";


const HeaderTable = () => {
  const {
    tables,
    activeTableId,
    setActiveTableId,
    addTable,
    addTableFromTemplate,
    renameTable,
    removeTable,
    confirm,
  } = useContext(ProjectContext);

  const { clear } = useContext(PdfHighlightContext);

  /* ---------------- Estados para menús y loaders ------------------------ */
  const [openOptionsFor, setOpenOptionsFor] = useState(null);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const [loadingRenameId, setLoadingRenameId] = useState(null);
  const [loadingDeleteId, setLoadingDeleteId] = useState(null);

  /* Dropdown “+” */
  const [isPlusOpen, setIsPlusOpen] = useState(false);
  const [plusMenuPos, setPlusMenuPos] = useState({ top: 0, left: 0 });
  const plusRef = useRef(null);
  const [isCreating, setIsCreating] = useState(false);

  /* Templates */
  const [templates, setTemplates] = useState([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [cloningTemplateId, setCloningTemplateId] = useState(null);

  /* 🤖 Siempre descargar templates al abrir “+” */
  useEffect(() => {
    if (!isPlusOpen) return;
    const fetchTemplates = async () => {
      setIsLoadingTemplates(true);
      try {
        const { templates: t } = await getTemplatesAPI(50);
        setTemplates(t);
      } catch {
        setTemplates([]);
      } finally {
        setIsLoadingTemplates(false);
      }
    };
    fetchTemplates();
  }, [isPlusOpen]);

  /* Portal para menús flotantes */
  const Portal = ({ children }) => ReactDOM.createPortal(children, document.body);

  /* ---------------------- Tabs options (rename / delete) ---------------- */
  const toggleOptionsMenu = (e, tableId) => {
    setOpenOptionsFor(curr => (curr === tableId ? null : tableId));
    if (openOptionsFor !== tableId) {
      const rect = e.currentTarget.getBoundingClientRect();
      setMenuPos({ top: rect.top + rect.height / 2, left: rect.right + 8 });
    }
  };

  const commitRename = async (id) => {
    const newName = renameValue.trim();
    if (!newName) return setRenamingId(null);
    setLoadingRenameId(id);
    try {
      await renameTable(id, newName);
    } finally {
      setLoadingRenameId(null);
      setRenamingId(null);
    }
  };

  /* ----------------------- Dropdown “+” logic --------------------------- */
  const togglePlusDropdown = () => {
    if (isCreating) return;
    setIsPlusOpen(curr => !curr);
    if (!isPlusOpen && plusRef.current) {
      const rect = plusRef.current.getBoundingClientRect();
      setPlusMenuPos({ top: rect.bottom + 4, left: rect.left });
    }
  };

  /* -------------------- Click fuera para cerrar menús ------------------- */
  useEffect(() => {
    const onOut = (e) => {
      if (
        openOptionsFor &&
        !e.target.closest(".options-menu") &&
        !e.target.closest(".options-btn")
      ) setOpenOptionsFor(null);

      if (
        isPlusOpen &&
        !e.target.closest(".plus-dropdown") &&
        !e.target.closest(".plus-tab")
      ) setIsPlusOpen(false);
    };
    document.addEventListener("mousedown", onOut);
    return () => document.removeEventListener("mousedown", onOut);
  }, [openOptionsFor, isPlusOpen]);

  return (
    <div className="header-table-container">
      <div className="tabs">
        {tables.map(table => {
          const isActive = activeTableId === table.id;
          const isRen = renamingId === table.id;
          const isOptOpen = openOptionsFor === table.id;
          const isLocked = table.extractionStatus === "in-progress";

          return (
            <div key={table.id} className={`tab-item ${isActive ? "active" : ""}`}>
              {isRen ? (
                <input
                className="tab-name-input"
                value={renameValue}
                onChange={e => setRenameValue(e.target.value)}
                onBlur={e => {
                  // Si se está presionando Enter, ignorar el blur para evitar doble ejecución
                  if (e.relatedTarget && e.relatedTarget.dataset?.trigger === "rename-enter") return;
                  commitRename(table.id);
                }}
                onKeyDown={e => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    commitRename(table.id);
                  } else if (e.key === "Escape") {
                    setRenamingId(null);
                  }
                }}
                autoFocus
              />              
              ) : (
                <div
                  className="tab-name"
                  onClick={() => {
                    setActiveTableId(table.id);
                    clear(); // 👈 limpia las referencias al cambiar de tabla
                  }}
                >
                  {table.name}
                  {isLocked && (
                    <div
                      className="spinner-border spinner-border-sm ms-2"
                      role="status"
                      title="Extraction in progress"
                      style={{ width: '0.9rem', height: '0.9rem' }}
                    />
                  )}
                </div>
              )}
              {!isLocked && (
                loadingRenameId === table.id || loadingDeleteId === table.id ? (
                  <div
                    className="spinner-border spinner-border-sm ms-2"
                    role="status"
                    title="Processing"
                    style={{ width: '1rem', height: '1rem' }}
                  />
                ) : (
                  <button
                    className="options-btn"
                    onClick={e => toggleOptionsMenu(e, table.id)}
                  >
                    <i className="bi bi-three-dots-vertical" />
                  </button>
                )
              )}
              {isOptOpen && (
                <Portal>
                  <div
                    className="options-menu"
                    style={{ position: "fixed", top: menuPos.top, left: menuPos.left }}
                  >
                    <div
                      className="options-menu-item"
                      onClick={() => {
                        setRenamingId(table.id);
                        setRenameValue(table.name);
                        setOpenOptionsFor(null);
                      }}
                    >
                      {loadingRenameId === table.id ? (
                        <div className="spinner-border spinner-border-sm" role="status" />
                      ) : (
                        <>Rename <i className="bi bi-pencil-square" /></>
                      )}
                    </div>
                    <div
                      className="options-menu-item"
                      onClick={async () => {
                        const confirmed = await confirm({
                          title: "Delete table?",
                          message: `Are you sure you want to delete the table "${table.name}"? This action cannot be undone.`,
                        });

                        if (!confirmed) return;

                        setLoadingDeleteId(table.id);
                        try {
                          await removeTable(table.id);
                        } finally {
                          setLoadingDeleteId(null);
                          setOpenOptionsFor(null);
                        }
                      }}
                    >

                      {loadingDeleteId === table.id ? (
                        <div className="spinner-border spinner-border-sm" role="status" />
                      ) : (
                        <>Delete table <i className="bi bi-trash" /></>
                      )}
                    </div>
                  </div>
                </Portal>
              )}
            </div>
          );
        })}

        {/* "+" tab */}
        <div
          ref={plusRef}
          className="tab-item plus-tab"
          onClick={togglePlusDropdown}
        >
          <div className="icon-button">
            {isCreating ? (
              <div className="spinner-border spinner-border-sm" role="status" />
            ) : (
              <>
                <i className="bi bi-plus-lg"></i>
                <span className="button-tooltip">Add table</span>
              </>
            )}
          </div>
        </div>
        {isPlusOpen && !isCreating && (
          <Portal>
            <div
              className="plus-dropdown"
              style={{ position: "fixed", top: plusMenuPos.top, left: plusMenuPos.left }}
            >
              <div
                className="plus-dropdown-item"
                onClick={async () => {
                  setIsPlusOpen(false);
                  setIsCreating(true);
                  try { await addTable(); } finally { setIsCreating(false); }
                }}
              >
                Blank Table
              </div>
              <div className="plus-dropdown-title">Templates</div>
              <div className="plus-templates">
                {isLoadingTemplates ? (
                  <div className="spinner-border spinner-border-sm" role="status" />
                ) : templates.length === 0 ? (
                  <div className="plus-dropdown-empty">No templates</div>
                ) : (
                  templates.map(t => (
                    <div
                      key={t.id}
                      className="plus-dropdown-item template-item d-flex justify-content-between align-items-center"
                    >
                      <span
                        onClick={async () => {
                          if (cloningTemplateId) return;
                          setCloningTemplateId(t.id);
                          try {
                            await addTableFromTemplate(t.id, t.name);
                            setIsPlusOpen(false);
                          } finally {
                            setCloningTemplateId(null);
                          }
                        }}
                        style={{ flex: 1, cursor: "pointer" }}
                      >
                        {cloningTemplateId === t.id ? (
                          <div className="spinner-border spinner-border-sm" role="status" />
                        ) : (
                          t.name
                        )}
                      </span>
                  
                      <button
                        className="btn btn-sm btn-link text-danger"
                        onClick={async (e) => {
                          e.stopPropagation(); // evita disparar el clonado al hacer click en el botón
                        
                          const confirmed = await confirm({
                            title: "Delete template?",
                            message: `Are you sure you want to delete the template "${t.name}"? This action cannot be undone.`,
                          });
                        
                          if (!confirmed) return;
                        
                          try {
                            await deleteTemplateAPI(t.id);
                            setTemplates(prev => prev.filter(temp => temp.id !== t.id));
                          } catch (err) {
                            console.error("Error deleting template:", err);
                            alert("Delete template");
                          }
                        }}                        
                        title="Delete template"
                      >
                        <i className="bi bi-trash-fill" />
                      </button>
                    </div>
                  ))
                  
                )}
              </div>
            </div>
          </Portal>
        )}
      </div>
    </div>
  );
};

export default HeaderTable;
